'use strict';

const PREVIEW_CLASS = 'walk4friendship-signup-file-preview';
const ITEM_CLASS = 'walk4friendship-signup-file-preview__item';
const REMOVE_CLASS = 'walk4friendship-signup-file-preview__remove';
const THEME_DEFAULT_BTN = 'button.levcharity-signup-use-theme-default-logo';
const THEME_DEFAULT_FLAG = 'input.levcharity-feature-image-theme-default-flag';
const SIGNUP_FILE_SUBMIT_ACTIONS = [
    'levcharity_regular_signup',
    'levcharity_join_team',
    'levcharity_create_team',
    'levcharity_join_campaign',
];
/** Soft client limit aligned with common WP upload caps; server still enforces wp_max_upload_size(). */
const SIGNUP_MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

function isFeaturedFileInput(input) {
    if (!input || input.type !== 'file' || input.multiple) {
        return false;
    }
    const name = input.name || '';
    return name === 'featured_image' || name === 'team_featured_image';
}

function isAdditionalFileInput(input) {
    return Boolean(input?.matches?.('input[type="file"][name*="additional_image"]'));
}

function getPreviewContainer(group) {
    return group?.querySelector?.(`.${PREVIEW_CLASS}`) ?? null;
}

function ensurePreviewContainer(group) {
    let el = getPreviewContainer(group);
    if (el) {
        return el;
    }
    el = document.createElement('div');
    el.className = PREVIEW_CLASS;
    const anchor =
        group.querySelector('button.upload-image-button') ||
        group.querySelector('input[type="file"]');
    anchor?.insertAdjacentElement('afterend', el);
    return el;
}

function revokePreviewUrls(container) {
    if (!container) {
        return;
    }
    container.querySelectorAll('img[data-object-url]').forEach((img) => {
        const u = img.getAttribute('data-object-url');
        if (u) {
            URL.revokeObjectURL(u);
        }
    });
}

function updateUploadButtonFilesText(group, fileInput) {
    const btn = group?.querySelector('button.upload-image-button');
    if (!btn || !fileInput) {
        return;
    }
    const n = fileInput.files?.length ?? 0;
    if (n > 1) {
        btn.dataset.files = `${n}Files`;
    } else if (n === 1) {
        btn.dataset.files = fileInput.files[0].name;
    } else {
        btn.removeAttribute('data-files');
    }
}

function setThemeDefaultFlag(group, value) {
    const flag = group?.querySelector(THEME_DEFAULT_FLAG);
    if (flag) {
        flag.value = value;
    }
    const defaultBtn = group?.querySelector(THEME_DEFAULT_BTN);
    if (defaultBtn) {
        const on = value === '1';
        defaultBtn.classList.toggle('is-selected', on);
        defaultBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
}

function clearPreviewContainer(container, clearThemeDefaultFlag = true) {
    if (!container) {
        return;
    }
    revokePreviewUrls(container);
    container.innerHTML = '';
    container.hidden = true;
    const group = container.closest('.form-group');
    if (clearThemeDefaultFlag) {
        setThemeDefaultFlag(group, '');
    }
    const fileInput =
        group?.querySelector('input[type="file"][name*="additional_image"]') ||
        group?.querySelector('input[name="featured_image"], input[name="team_featured_image"]');
    if (fileInput) {
        updateUploadButtonFilesText(group, fileInput);
    }
}

function makeRemoveButton() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = REMOVE_CLASS;
    btn.setAttribute('aria-label', 'Remove image');
    btn.textContent = '×';
    return btn;
}

function renderFeaturedPreview(group, fileInput, file, container) {
    clearPreviewContainer(container);
    if (!file) {
        return;
    }
    const url = URL.createObjectURL(file);
    const item = document.createElement('div');
    item.className = ITEM_CLASS;
    const img = document.createElement('img');
    img.src = url;
    img.alt = file.name;
    img.setAttribute('data-object-url', url);
    const rm = makeRemoveButton();
    item.append(img, rm);
    container.appendChild(item);
    container.hidden = false;
    updateUploadButtonFilesText(group, fileInput);
    syncFeaturedImageToForm(group, url);
}

function getFeaturedDataName(fileInput) {
    if (!fileInput) {
        return 'featured_image';
    }
    return fileInput.name === 'team_featured_image' ? 'team_featured_image' : 'featured_image';
}

function isLikelyPageUrlNotImage(url = '') {
    if (!url) {
        return true;
    }
    try {
        const parsed = new URL(url, window.location.origin);
        if (/\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i.test(parsed.pathname)) {
            return false;
        }
        return parsed.pathname.includes('/participate/');
    } catch {
        return false;
    }
}

function readTeamCardCookie() {
    const match = document.cookie.match(/(?:^|;\s*)team_card_data=([^;]*)/);
    if (!match?.[1]) {
        return {};
    }
    try {
        return JSON.parse(decodeURIComponent(match[1])) || {};
    } catch {
        return {};
    }
}

function writeTeamCardCookie(patch = {}) {
    const card = {...readTeamCardCookie(), ...patch};
    document.cookie = `team_card_data=${encodeURIComponent(JSON.stringify(card))}; path=/;`;
}

function syncFeaturedImageToForm(group, imageUrl) {
    if (!(group instanceof HTMLElement) || !imageUrl || isLikelyPageUrlNotImage(imageUrl)) {
        return;
    }

    const wrapper = group.closest('.levcharity-multi-step-form-wrapper');
    const fileInput =
        group.querySelector('input[type="file"][name="team_featured_image"]') ||
        group.querySelector('input[type="file"][name="featured_image"]');
    const dataName = getFeaturedDataName(fileInput);

    wrapper
        ?.querySelectorAll(`img[data-name="${dataName}"]`)
        .forEach((img) => {
            if (img instanceof HTMLImageElement) {
                img.src = imageUrl;
            }
        });

    let tracker = group.querySelector('img.w4f-featured-image-tracker');
    if (!(tracker instanceof HTMLImageElement)) {
        tracker = document.createElement('img');
        tracker.className = 'w4f-featured-image-tracker';
        tracker.hidden = true;
        tracker.alt = '';
        tracker.setAttribute('data-name', dataName);
        group.appendChild(tracker);
    }
    tracker.src = imageUrl;

    const teamName = group.closest('.signup-form-wrapper')?.querySelector('input[name="team_name"]')?.value?.trim() || readTeamCardCookie().name || '';
    writeTeamCardCookie({
        name: teamName,
        image_src: imageUrl,
    });

    wrapper?.querySelectorAll('.preview.team .img-wrapper img').forEach((img) => {
        if (img instanceof HTMLImageElement) {
            img.src = imageUrl;
        }
    });
}

function renderFeaturedUrlPreview(group, fileInput, imageUrl, container) {
    if (!imageUrl) {
        return;
    }

    const previewContainer = container || getPreviewContainer(group);
    if (previewContainer) {
        clearPreviewContainer(previewContainer, false);
    }

    if (fileInput) {
        fileInput.value = '';
        forgetSignupFilesForInput(fileInput);
        updateUploadButtonFilesText(group, fileInput);
    }
    setThemeDefaultFlag(group, '1');
    syncFeaturedImageToForm(group, imageUrl);
}

function renderAdditionalPreviews(group, fileInput, container) {
    clearPreviewContainer(container);
    const files = fileInput.files ? Array.from(fileInput.files) : [];
    if (files.length === 0) {
        return;
    }
    files.forEach((file, index) => {
        const url = URL.createObjectURL(file);
        const item = document.createElement('div');
        item.className = ITEM_CLASS;
        item.dataset.fileIndex = String(index);
        const img = document.createElement('img');
        img.src = url;
        img.alt = file.name;
        img.setAttribute('data-object-url', url);
        const rm = makeRemoveButton();
        item.append(img, rm);
        container.appendChild(item);
    });
    container.hidden = false;
    updateUploadButtonFilesText(group, fileInput);
}

function formatSignupMaxUploadSize() {
    const mb = Math.round(SIGNUP_MAX_UPLOAD_BYTES / (1024 * 1024));
    return `${mb}MB`;
}

function getSignupFileTooLargeMessage() {
    const sizeLabel = formatSignupMaxUploadSize();
    if (typeof wp !== 'undefined' && wp.i18n) {
        return wp.i18n.sprintf(
            wp.i18n.__('This image is too large. Please choose a file under %s.', 'walk4friendship'),
            sizeLabel
        );
    }
    return `This image is too large. Please choose a file under ${sizeLabel}.`;
}

function rejectOversizedSignupFiles(input) {
    if (!input?.files?.length) {
        return true;
    }

    const files = Array.from(input.files);
    const oversized = files.filter((file) => file.size > SIGNUP_MAX_UPLOAD_BYTES);
    if (!oversized.length) {
        return true;
    }

    input.value = '';
    forgetSignupFilesForInput(input);

    showSignupImageError(getSignupFileTooLargeMessage());

    return false;
}

function showSignupImageError(message) {
    if (typeof window === 'undefined') {
        return;
    }

    // Match MultiStepForm / signup error UX (toast, not browser alert).
    if (window.levToast?.error) {
        window.levToast.error(message);
        return;
    }

    if (window.Toast?.error) {
        window.Toast.error(message);
    }
}

function handleFileChange(root, event) {
    const input = event.target;
    if (!input.matches?.('input[type="file"]') || !root.contains(input)) {
        return;
    }
    const group = input.closest('.form-group');
    if (!group) {
        return;
    }

    if (!rejectOversizedSignupFiles(input)) {
        const container = getPreviewContainer(group);
        if (container) {
            clearPreviewContainer(container);
        }
        updateUploadButtonFilesText(group, input);
        return;
    }

    if (isFeaturedFileInput(input)) {
        const container = ensurePreviewContainer(group);
        if (!input.files?.length) {
            clearPreviewContainer(container);
            return;
        }
        setThemeDefaultFlag(group, '');
        forgetSignupFilesForInput(input);
        renderFeaturedPreview(group, input, input.files[0], container);
        return;
    }

    if (isAdditionalFileInput(input)) {
        const container = ensurePreviewContainer(group);
        renderAdditionalPreviews(group, input, container);
    }
}

function handleRemoveDelegated(root, event) {
    const btn = event.target.closest?.(`button.${REMOVE_CLASS}`);
    if (!btn || !root.contains(btn)) {
        return;
    }
    const item = btn.closest(`.${ITEM_CLASS}`);
    const container = btn.closest(`.${PREVIEW_CLASS}`);
    if (!item || !container) {
        return;
    }
    const group = container.closest('.form-group');
    const fileInput =
        group?.querySelector('input[type="file"][name*="additional_image"]') ||
        group?.querySelector(
            'input[name="featured_image"], input[name="team_featured_image"]',
        );
    if (!fileInput || !root.contains(fileInput)) {
        return;
    }

    if (isAdditionalFileInput(fileInput)) {
        const idx = parseInt(item.dataset.fileIndex ?? '', 10);
        if (Number.isNaN(idx)) {
            return;
        }
        const dt = new DataTransfer();
        Array.from(fileInput.files).forEach((f, i) => {
            if (i !== idx) {
                dt.items.add(f);
            }
        });
        fileInput.files = dt.files;
        renderAdditionalPreviews(group, fileInput, container);
        return;
    }

    if (isFeaturedFileInput(fileInput)) {
        fileInput.value = '';
        forgetSignupFilesForInput(fileInput);
        clearPreviewContainer(container);
    }
}

function handleThemeDefaultLogoClick(root, event) {
    const button = event.target.closest?.(THEME_DEFAULT_BTN);
    if (!button || !root.contains(button)) {
        return;
    }
    event.preventDefault();
    event.stopPropagation();
    const group = button.closest('.form-group');
    const fileInput = group?.querySelector(
        'input[type="file"][name="featured_image"], input[type="file"][name="team_featured_image"]',
    );
    if (!fileInput || !isFeaturedFileInput(fileInput)) {
        return;
    }

    if (button.classList.contains('is-selected')) {
        setThemeDefaultFlag(group, '');
        clearPreviewContainer(getPreviewContainer(group));
        updateUploadButtonFilesText(group, fileInput);
        writeTeamCardCookie({ image_src: '' });
        return;
    }

    const src = button.getAttribute('data-preview-src');
    if (!src) {
        return;
    }
    renderFeaturedUrlPreview(group, fileInput, src, getPreviewContainer(group));
}

function applyPersistedFeaturedImageToStep(step) {
    if (!(step instanceof HTMLElement)) {
        return;
    }

    const card = readTeamCardCookie();
    const imageSrc = card.image_src || '';
    if (!imageSrc || isLikelyPageUrlNotImage(imageSrc)) {
        return;
    }

    step.querySelectorAll('.form-group').forEach((group) => {
        if (!(group instanceof HTMLElement)) {
            return;
        }

        const fileInput =
            group.querySelector('input[type="file"][name="team_featured_image"]') ||
            group.querySelector('input[type="file"][name="featured_image"]');
        if (!fileInput) {
            return;
        }

        const dataName = getFeaturedDataName(fileInput);
        group.querySelectorAll(`img[data-name="${dataName}"]`).forEach((img) => {
            if (img instanceof HTMLImageElement) {
                img.src = imageSrc;
            }
        });

        const themeDefaultSelected = group.querySelector(THEME_DEFAULT_FLAG)?.value === '1';
        if (themeDefaultSelected) {
            renderFeaturedUrlPreview(group, fileInput, imageSrc, getPreviewContainer(group));
        }
    });
}

function handleUploadButtonCapture(root, event) {
    const button = event.target.closest?.('button.upload-image-button');
    if (!button || !root.contains(button)) {
        return;
    }

    const group = button.closest('.form-group');
    const additionalInput = group?.querySelector('input[type="file"][name*="additional_image"]');
    const featuredInput = group?.querySelector(
        'input[type="file"][name="featured_image"], input[type="file"][name="team_featured_image"]',
    );
    const fileInput = additionalInput || featuredInput;
    if (!fileInput) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    fileInput.value = '';
    fileInput.click();
}

const signupSelectedFiles = new Map();

function rememberSignupFile(input) {
    if (!input?.name || !input.files?.length) {
        return;
    }

    Array.from(input.files).forEach((file, index) => {
        const key = index > 0 ? `${input.name}[]` : input.name;
        signupSelectedFiles.set(key, file);
    });
}

function forgetSignupFilesForInput(input) {
    if (!input?.name) {
        return;
    }

    signupSelectedFiles.delete(input.name);
    signupSelectedFiles.delete(`${input.name}[]`);
}

function resolveSignupAjaxAction(url, formData) {
    const fromBody = String(formData.get('action') || '');
    if (fromBody) {
        return fromBody;
    }

    try {
        return new URL(String(url), window.location.origin).searchParams.get('action') || '';
    } catch (error) {
        return '';
    }
}

function appendSignupFileToFormData(formData, name, file) {
    if (!(file instanceof File) || file.size <= 0) {
        return;
    }

    const existing = formData.get(name);
    if (existing instanceof File && existing.size > 0) {
        return;
    }

    formData.delete(name);
    formData.append(name, file, file.name);
}

function appendSignupFilesFromEarlierSteps(root, formData, url) {
    const action = resolveSignupAjaxAction(url, formData);
    if (!action || !SIGNUP_FILE_SUBMIT_ACTIONS.includes(action)) {
        return;
    }

    root.querySelectorAll('form[data-form-id] input[type="file"][name]').forEach((fileInput) => {
        if (fileInput.files?.length) {
            rememberSignupFile(fileInput);
        }

        if (fileInput.files?.length) {
            Array.from(fileInput.files).forEach((file, index) => {
                if (file.size > SIGNUP_MAX_UPLOAD_BYTES) {
                    return;
                }
                const fieldName = fileInput.multiple && index > 0 ? `${fileInput.name}[]` : fileInput.name;
                appendSignupFileToFormData(formData, fieldName, file);
            });
            return;
        }

        const remembered = signupSelectedFiles.get(fileInput.name);
        if (remembered && remembered.size <= SIGNUP_MAX_UPLOAD_BYTES) {
            appendSignupFileToFormData(formData, fileInput.name, remembered);
        }
    });

    signupSelectedFiles.forEach((file, name) => {
        if (file.size > SIGNUP_MAX_UPLOAD_BYTES) {
            return;
        }
        appendSignupFileToFormData(formData, name, file);
    });

    root.querySelectorAll('input.levcharity-feature-image-theme-default-flag[name]').forEach((flag) => {
        if (flag.value && !formData.has(flag.name)) {
            formData.append(flag.name, flag.value);
        }
    });
}

let signupFetchPatched = false;

function patchSignupFetch(root) {
    if (signupFetchPatched) {
        return;
    }

    signupFetchPatched = true;
    const originalFetch = window.fetch.bind(window);

    window.fetch = (url, options = {}) => {
        const body = options?.body;
        if (body instanceof FormData && String(url).includes('admin-ajax')) {
            appendSignupFilesFromEarlierSteps(root, body, url);
        }

        return originalFetch(url, options);
    };
}

export function initSignupImageUpload() {
    const root = document.querySelector('.levcharity-multi-step-form-wrapper');
    if (!root) {
        return;
    }

    patchSignupFetch(root);

    root.addEventListener('click', (e) => handleThemeDefaultLogoClick(root, e), true);
    root.addEventListener('click', (e) => handleUploadButtonCapture(root, e), true);
    root.addEventListener('change', (e) => handleFileChange(root, e));
    root.addEventListener('click', (e) => handleRemoveDelegated(root, e));

    applyPersistedFeaturedImageToStep(root.querySelector('.signup-form-wrapper.current-step'));

    if (typeof MutationObserver !== 'undefined') {
        const stepObserver = new MutationObserver(() => {
            applyPersistedFeaturedImageToStep(root.querySelector('.signup-form-wrapper.current-step'));
        });
        stepObserver.observe(root, {
            attributes: true,
            attributeFilter: ['class'],
            subtree: true,
        });
    }
}
